import { SizeButton } from '@/constants/sizeButton'
import { lightTheme } from '@/customTheme'
import Back from '@/scenes/Editor/components/Icons/Back'
import { ThemeProvider } from 'baseui'
import { KIND, SHAPE } from 'baseui/button'
import ButtonCustom from '../ButtonCustom'
import DefaultButton from '../DefaultButton/DefaultButton'
import Icons from '@/scenes/Editor/components/Icons'
import { useTranslation } from 'react-i18next'
import { CSSProperties, ReactNode } from 'react'
import { setShowModalCustomText } from '@/store/slices/modal/customTextModal'

function HeadingInspector({
  hasBoxShadow,
  hasNavigation,
  handleNavigation,
  title,
  hasClose,
  handleClose,
  style,
  hasQuestionMarkButton,
  questionMarkButtonAction,
  showDiscardModalOnChanges = false,
  discardModalHeader,
  discardModalDescription,
  areChangesToDiscard = false,
  disableNavigation = false,
  compactTitle = false,
  titleStyle,
  hasReset = false,
  onReset,
  isResetActive = false,
  resetLabel,
  endAction,
}: {
  hasBoxShadow?: boolean
  hasNavigation: boolean
  handleNavigation?: () => void
  title: string
  hasClose?: boolean
  handleClose?: () => void
  style?: CSSProperties
  hasQuestionMarkButton?: boolean
  questionMarkButtonAction?: ()=>void
  showDiscardModalOnChanges?: boolean
  discardModalHeader?: string
  discardModalDescription?: string
  areChangesToDiscard?: boolean
  disableNavigation?: boolean
  compactTitle?: boolean
  titleStyle?: CSSProperties
  /** Shows a Reset button in the header (e.g. Adjust) instead of one inline in the panel content. */
  hasReset?: boolean
  onReset?: () => void
  /** Whether Reset is currently actionable (mirrors the footer's isPrimaryButtonActive convention). */
  isResetActive?: boolean
  /** Overrides the Reset button's label (e.g. 'Clear'). Defaults to 'Reset'. */
  resetLabel?: string
  /** An optional trailing action, alongside the existing header controls. */
  endAction?: ReactNode
}) {
  const { t } = useTranslation()
  return (
    <ThemeProvider theme={lightTheme}>
      <div
        className={ style ? '' : 'h-heading-inspector'}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '8px',
          alignItems: 'center',
          paddingLeft: hasNavigation ? '16px' : '24px',
          paddingRight: '16px',
          boxShadow: hasBoxShadow ? '0px 1px 6px rgba(0, 0, 0, 0.1)' : 'none',
          position: 'relative',
          zIndex: 1,
          ...style
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', columnGap: 'inherit', flex: 1, minWidth: 0}}>
          {hasNavigation && (
            <ButtonCustom
              shape={SHAPE.square}
              kind={KIND.minimal}
              type={SizeButton.SMALL}
              disabled={disableNavigation}
              onClick={()=> {
                if (showDiscardModalOnChanges && areChangesToDiscard) {
                  setShowModalCustomText(
                    discardModalHeader,
                    discardModalDescription,
                    t('Discard'),
                    t('Cancel'),
                    true,
                    handleNavigation,
                    () => {}
                  )
                }
                else {
                  handleNavigation()
                }
              }}
              style={{borderRadius: '360px', flexShrink: 0}}
            >
              <Back size={24} disabled={disableNavigation}/>
            </ButtonCustom>
          )}
          <p
            style={{
              height: compactTitle ? '24px' : '32px',
              width: '100%',
              ...(compactTitle ? lightTheme.typography.Small14Semibold : lightTheme.typography.Header20bold),
              color: lightTheme.colors.blackGray,
              textTransform: 'capitalize',
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              ...titleStyle,
            }}
          >
            {t(title)}
          </p>
        </div>
        {hasReset && (
          <DefaultButton
            type="minimal"
            size="small"
            label={resetLabel ?? t('Reset')}
            disabled={!isResetActive}
            onClick={onReset}
          />
        )}
        {hasClose && (
          <ButtonCustom
            shape={SHAPE.square}
            kind={KIND.minimal}
            type={SizeButton.SMALL}
            onClick={handleClose}
            style={{ borderRadius: '360px'}}
          >
            <Icons.Close size={24} />
          </ButtonCustom>
        )}
        { hasQuestionMarkButton &&
          <ButtonCustom
          style={{  width: '32px', padding: 0 }}
          type={SizeButton.SMALL}
          kind={KIND.minimal}
          onClick={() => {
            questionMarkButtonAction()
          }}
        >
          <Icons.HelpIcon />
        </ButtonCustom>
        }
        {endAction}
      </div>
    </ThemeProvider>
  )
}

export default HeadingInspector
